import {z} from 'zod'
import {httpGetPatients, HttpGetPatientsOptions} from '@/features/patients/services/patients.http.ts'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {useEffect, useState} from 'react'
import {captureException} from '@sentry/react'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {QUERY_KEYS} from '@/queryClient.ts'
import {PaginationResponse} from '@/types.ts'
import {PatientSmall} from '@/features/patients/types.ts'

const ResponseShape = z.object({data: z.array(PatientSmall), meta: PaginationResponse})
type ResponseShape = z.infer<typeof ResponseShape>

export const usePatients = (
    params: HttpGetPatientsOptions['params'],
    payload?: HttpGetPatientsOptions['payload'],
    options?: Omit<UseQueryOptions<ResponseShape>, 'queryKey' | 'queryFn'>
) => {
    const [persistedPagination, setPersistedPagination] = useState<PaginationResponse>({
        total: 0,
        lastPage: 1,
        perPage: 50,
        currentPage: 1
    })

    const query = useQuery({
        queryKey: [QUERY_KEYS.PATIENTS, params, payload],
        queryFn: () =>
            parseAxiosPromise({
                axiosPromise: httpGetPatients({params, payload}),
                onZodError: captureException,
                responseShape: ResponseShape
            }),
        ...options
    })

    useEffect(() => {
        if (query.isSuccess) {
            setPersistedPagination(query.data.meta)
        }
    }, [query.data?.data.length])

    return {...query, persistedPagination}
}
